import type { Breadcrumb, Event, replayIntegration } from "@sentry/nextjs";
import {
	hasSensitiveQueryParam,
	redactSensitiveQueryParams,
} from "@jasperlabs/utils";

// Where Sentry writes page URLs. Each export below covers one of them.
const BREADCRUMB_URL_FIELDS = ["url", "from", "to"];
const REPLAY_FRAME_URL_FIELDS = ["url", "previous"];

type ReplayOptions = NonNullable<Parameters<typeof replayIntegration>[0]>;

function redactFields(
	record: Record<string, unknown> | undefined,
	fields: string[],
): void {
	if (!record) {
		return;
	}
	for (const field of fields) {
		const value = record[field];
		if (typeof value === "string") {
			record[field] = redactSensitiveQueryParams(value);
		}
	}
}

export function redactBreadcrumb(crumb: Breadcrumb): Breadcrumb {
	redactFields(crumb.data, BREADCRUMB_URL_FIELDS);
	return crumb;
}

// Runs for error and replay events; beforeSend never sees replays.
export function redactEvent(event: Event): Event {
	if (event.request?.url) {
		event.request.url = redactSensitiveQueryParams(event.request.url);
	}
	redactFields(event.request?.headers, ["Referer"]);
	if ("urls" in event && Array.isArray(event.urls)) {
		event.urls = event.urls.map((url) => redactSensitiveQueryParams(url));
	}
	return event;
}

export const replayRedactionOptions: Required<
	Pick<ReplayOptions, "beforeErrorSampling" | "beforeAddRecordingEvent">
> = {
	// The recording's rrweb meta frame holds the raw page URL and no hook can
	// reach it, so send no error replay while a secret is in the URL.
	beforeErrorSampling: () => !hasSensitiveQueryParam(window.location.search),
	// Only custom frames reach this hook: navigation spans and click crumbs.
	beforeAddRecordingEvent(event) {
		const payload = event.data.payload as {
			description?: unknown;
			data?: Record<string, unknown>;
		};
		if (typeof payload.description === "string") {
			payload.description = redactSensitiveQueryParams(payload.description);
		}
		redactFields(payload.data, REPLAY_FRAME_URL_FIELDS);
		return event;
	},
};
